import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';

import {
  IStationeryDispatchDetailedItem,
  IStationeryDispatchListItem,
  IStationeryInsufficientInventoryItem,
} from 'src/types/stationery-dispatch';

import axios from '../../utils/axios';

interface IInitialState {
  status: 'idle' | 'loading' | 'success' | 'failure';
  error: string | undefined;
  dispatches: {
    count: number;
    page: number;
    pageSize: number;
    pageCount: number;
    items: IStationeryDispatchListItem[];
  };
  dispatch: IStationeryDispatchDetailedItem | null;
}

const initialState: IInitialState = {
  status: 'idle',
  error: undefined,
  dispatches: {
    count: 0,
    page: 0,
    pageSize: 0,
    pageCount: 0,
    items: [],
  },
  dispatch: null,
};

const stationeryDispatchSlice = createSlice({
  name: 'stationeryDispatch',
  initialState,
  reducers: {
    removeStationeryDispatch: (state, action) => {
      const { dispatchId } = action.payload;

      const index = state.dispatches.items.findIndex((item) => item._id === dispatchId);

      if (index > -1) {
        action.payload.index = index;
        state.dispatches.items = state.dispatches.items.filter(
          (item) => item._id !== dispatchId
        );
      }
    },
    rollbackStationeryDispatchDeletion: (state, action) => {
      const { item, index } = action.payload;

      state.dispatches.items.splice(index, 0, item);
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(getStationeryDispatches.pending, (state) => {
        state.status = 'loading';
      })
      .addCase(getStationeryDispatches.fulfilled, (state, action) => {
        state.status = 'success';
        state.dispatches = action.payload;
      })
      .addCase(getStationeryDispatches.rejected, (state, action) => {
        state.dispatches = initialState.dispatches;
        state.status = 'failure';
        state.error = action.error.message;
      })
      .addCase(getStationeryDispatchById.pending, (state) => {
        state.status = 'loading';
      })
      .addCase(getStationeryDispatchById.fulfilled, (state, action) => {
        state.status = 'success';
        state.dispatch = action.payload;
        state.error = initialState.error;
      })
      .addCase(getStationeryDispatchById.rejected, (state, action) => {
        state.dispatch = initialState.dispatch;
        state.status = 'failure';
        state.error = action.error.message;
      });
  },
});

export default stationeryDispatchSlice.reducer;

export const { removeStationeryDispatch, rollbackStationeryDispatchDeletion } =
  stationeryDispatchSlice.actions;

export const getStationeryDispatches = createAsyncThunk(
  'stationeryDispatch/paginated',
  async (params: {
    page: number;
    limit: number;
    generalSearch?: string | null;
    issuedBy?: string | null;
    receivedBy?: string | null;
    startDate?: Date | null;
    endDate?: Date | null;
    sortBy?: string | null;
    sort?: 'asc' | 'desc' | null;
  }) => {
    const { page, limit, generalSearch, issuedBy, receivedBy, startDate, endDate, sortBy, sort } =
      params;

    try {
      const response = await axios.get(
        `stationery/dispatch?page=${page + 1}&limit=${limit}&generalSearch=${
          generalSearch ?? ''
        }&issuedBy=${issuedBy ?? ''}&receivedBy=${receivedBy ?? ''}&startDate=${
          startDate ? startDate.toISOString() : ''
        }&endDate=${endDate ? endDate.toISOString() : ''}&sortBy=${sortBy}&sort=${sort ?? ''}`
      );

      return response.data.data;
    } catch (err) {
      console.log(err);
      throw err;
    }
  }
);

export const getStationeryDispatchById = createAsyncThunk(
  'stationeryDispatch/getById',
  async (params: { itemID: string }) => {
    try {
      const { itemID } = params;
      const response = await axios.get(`stationery/dispatch/${itemID}`);

      return response.data.data;
    } catch (err) {
      console.log(err);
      throw err;
    }
  }
);

export const deleteStationeryDispatch = createAsyncThunk(
  'stationeryDispatch/delete',
  async (params: { item: IStationeryDispatchListItem; index: number }, { dispatch }) => {
    const { item, index } = params;

    try {
      const response = await axios.delete(`stationery/dispatch/${item._id}`);
      return response.data.data;
    } catch (err) {
      dispatch(rollbackStationeryDispatchDeletion({ item, index }));
      throw err;
    }
  }
);

export interface TStationeryDispatchItemInput {
  // Present when editing an existing dispatch and this line is being kept
  // (its stock delta is adjusted in place server-side, since a Return line
  // may already reference this dispatch item's id). Always absent on
  // create, and absent on update for a brand-new line.
  _id?: string;
  stationeryItem: string;
  quantity: number;
  // Employee who received this specific line — per-line, not per-dispatch.
  receivedBy: string;
}

export interface TCreateStationeryDispatch {
  issuedBy: string;
  dispatchDate: Date;
  remarks: string | null;
  items: TStationeryDispatchItemInput[];
}

export type TUpdateStationeryDispatch = TCreateStationeryDispatch;

// Shape of the rejected value when create()/update() fails on
// insufficient inventory — rejectWithValue is used (rather than letting
// the thunk just throw) because Redux Toolkit's default error handling
// only keeps name/message/stack off a thrown Error, silently dropping the
// `.data` (IStationeryInsufficientInventoryItem[]) the axios interceptor
// attached; rejectWithValue's payload survives untouched through
// .unwrap().
export interface TStationeryDispatchInsufficientInventoryError {
  message: string;
  data: IStationeryInsufficientInventoryItem[] | null;
}

export const createStationeryDispatch = createAsyncThunk(
  'stationeryDispatch/create',
  async (
    params: { dispatchTransaction: TCreateStationeryDispatch },
    { rejectWithValue }
  ) => {
    const { dispatchTransaction } = params;
    try {
      const response = await axios.post('stationery/dispatch', dispatchTransaction);
      return response.data.data;
    } catch (err: any) {
      return rejectWithValue({ message: err.message, data: err.data ?? null });
    }
  }
);

export const updateStationeryDispatch = createAsyncThunk(
  'stationeryDispatch/update',
  async (
    params: { dispatchId: string; dispatchTransaction: TUpdateStationeryDispatch },
    { rejectWithValue }
  ) => {
    const { dispatchId, dispatchTransaction } = params;
    try {
      const response = await axios.put(`stationery/dispatch/${dispatchId}`, dispatchTransaction);
      return response.data.data;
    } catch (err: any) {
      return rejectWithValue({ message: err.message, data: err.data ?? null });
    }
  }
);
